import { useEffect } from 'react';
import { useEditWorkerSkillEntryCard } from './useEditWorkerSkillEntryCard';
import { Typography, SelectMenu, Switch, Button, Icon, TextField } from '@scale/scaleui';
import MultiSelect from 'corp/components/UI/Select';
import { SearchWorkerSkillsMenu } from 'react-scale/corp/views/LLMControlCenter/ProjectQualificationsComponents/SearchWorkerSkillsMenu';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { WorkerSkillDimensionQualityCategory, WorkerSkillEntryStatus } from '@scale/shared-types';
import { workerSkillTreeLevels } from 'react-scale/corp/views/LLMControlCenter/ProjectQualificationsComponents/constants';
import { useQualificationsStore } from 'react-scale/corp/views/LLMControlCenter/ProjectQualificationsComponents/store';
import { useFetchAllWorkerSkills } from 'react-scale/hooks/workerSkills/queries/useFetchAllWorkerSkills';
import { WorkerSkillEntry } from '@scale/shared/defaults';
import { twMerge } from 'tailwind-merge';

const MAX_SKILL_ENTRIES = 5;

const SkillRow = ({
  skillEntries,
  setSkillEntries,
  index,
  disabled,
}: {
  skillEntries: WorkerSkillEntry[];
  setSkillEntries: (entries: WorkerSkillEntry[]) => void;
  index: number;
  disabled: boolean;
}) => {
  const { setAllWorkerSkills } = useQualificationsStore();

  const isTree = true;
  const { data: allSkills, refetch: refetchSkills } = useFetchAllWorkerSkills(
    isTree,
    workerSkillTreeLevels,
    setAllWorkerSkills,
  );

  const handleChange = (field: keyof WorkerSkillEntry, value: any) => {
    const newEntries = [...skillEntries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setSkillEntries(newEntries);
  };
  const addSkill = () => {
    if (skillEntries.length < MAX_SKILL_ENTRIES) {
      setSkillEntries([
        ...skillEntries,
        { skill: { name: '', id: '0', children: [] }, tier: undefined },
      ]);
    }
  };

  const removeSkill = () => {
    const newEntries = skillEntries.filter((_, i) => i !== index);
    setSkillEntries(newEntries);
  };

  const isLastRow = index === skillEntries.length - 1;
  const canAddMore = skillEntries.length < MAX_SKILL_ENTRIES;
  const canRemove = skillEntries.length > 1;

  useEffect(() => {
    if (!allSkills) {
      refetchSkills();
    }
  }, [allSkills, refetchSkills]);

  return (
    <>
      <div className={twMerge('flex flex-col gap-4 rounded-lg w-full')}>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Typography.Paragraph2>Worker Skill*</Typography.Paragraph2>
            <SearchWorkerSkillsMenu
              onSelect={() => {}}
              className="w-full rounded-lg"
              onlyUseWorkerSkills
              selectedWorkerSkill={skillEntries[index].skill}
              onSelectWorkerSkill={skill => handleChange('skill', skill)}
              disabled={disabled}
            />
          </div>
          <div className="w-80">
            <Typography.Paragraph2>Skill Tier</Typography.Paragraph2>
            <SelectMenu
              className="w-full"
              placeholder="Select a skill tier..."
              options={Object.values(WorkerSkillDimensionQualityCategory).map(tier => ({
                id: tier,
                name: tier,
              }))}
              selectedOptionId={
                skillEntries[index].tier ?? WorkerSkillDimensionQualityCategory.SelfReported
              } // default tier to self-reported
              setSelected={tier =>
                handleChange('tier', tier as WorkerSkillDimensionQualityCategory)
              }
              disabled={disabled}
            />
          </div>
          <div className="w-80">
            <Typography.Paragraph2>Skill Status</Typography.Paragraph2>
            <SelectMenu
              className="w-full"
              placeholder="Select skill status..."
              options={Object.values(WorkerSkillEntryStatus).map(status => ({
                id: status,
                name: status,
              }))}
              selectedOptionId={skillEntries[index].status ?? WorkerSkillEntryStatus.SelfReported}
              setSelected={status => handleChange('status', status as WorkerSkillEntryStatus)}
              disabled={disabled}
            />
          </div>
          {canRemove && (
            <Icon
              icon={['fas', 'minus']}
              onClick={removeSkill}
              rounded
              size="sm"
              tooltip="Remove skill"
              variant="neutral"
            />
          )}
        </div>
      </div>
      {isLastRow && canAddMore && (
        <div className="flex flex-row gap-2">
          <Button
            icon={{
              position: 'left',
              type: ['fas', 'plus'],
            }}
            size="lg"
            onClick={addSkill}
            variant="transparent"
          >
            Add Skill Entry
          </Button>
        </div>
      )}
    </>
  );
};

const EditWorkerSkillEntryCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const {
    selectedColumnIdWithWorkerIds,
    setSelectedColumnIdWithWorkerIds,
    skillEntries,
    setSkillEntries,
    removeAllSkills,
    setRemoveAllSkills,
    overrideReason,
    setOverrideReason,
    // numWorkers,
    columns,
  } = useEditWorkerSkillEntryCard(index);

  return (
    <div className="flex flex-col gap-4">
      <Typography.Subtitle2>
        Edit worker skill entries for all selected workers
      </Typography.Subtitle2>
      <Typography.Paragraph2>
        Assign up to {MAX_SKILL_ENTRIES} worker skills to the selected workers, or remove all skills
        if you want to disable the workers.
      </Typography.Paragraph2>
      {/* {numWorkers > 0 && (
        <Typography.Paragraph2>
          {`Selected column has ${numWorkers} workers. Click update to edit their skills or disable them.`}
        </Typography.Paragraph2>
      )} */}
      <div className="flex flex-col gap-4 w-full">
        <MultiSelect
          className="w-80 rounded-lg"
          multi={false}
          options={columns.map(column => ({
            value: column.id ?? '-',
            label: column.header ?? '-',
          }))}
          disabled={removeAllSkills}
          placeholder="Select a column with worker ids"
          isSearchable={true}
          isClearable={true}
          value={selectedColumnIdWithWorkerIds}
          onChange={(e: any) => {
            setSelectedColumnIdWithWorkerIds(e === null ? null : e.value);
          }}
        />
        {skillEntries.map((_, idx) => (
          <SkillRow
            key={idx}
            skillEntries={skillEntries}
            setSkillEntries={setSkillEntries}
            index={idx}
            disabled={removeAllSkills}
          />
        ))}
        {skillEntries.length === 0 && (
          <Button
            onClick={() =>
              setSkillEntries([
                {
                  skill: { name: 'Search...', id: '0', children: [] },
                  tier: WorkerSkillDimensionQualityCategory.SelfReported,
                },
              ])
            }
            disabled={removeAllSkills}
            className="w-fit"
            icon={{
              position: 'left',
              type: ['fas', 'plus'],
            }}
            size="lg"
            variant="transparent"
          >
            Add Skill Entry
          </Button>
        )}
        <Switch
          label="Remove All Skills"
          checked={removeAllSkills}
          onCheckedChange={() => setRemoveAllSkills(!removeAllSkills)}
        />
        <div className="flex flex-col gap-2">
          <Typography.Paragraph2>Reason</Typography.Paragraph2>
          <TextField
            className="w-80"
            placeholder="Optional reason for skill changes..."
            value={overrideReason}
            onChange={e => setOverrideReason(e.target.value)}
            disabled={removeAllSkills}
          />
        </div>
      </div>
    </div>
  );
};

export default EditWorkerSkillEntryCardComponent;
